import { useContext } from 'react';
import { I18nContext } from '@/components/i18n/I18nProvider';
import itEntityTypes from '@/locales/it/entityTypes.json';
import enEntityTypes from '@/locales/en/entityTypes.json';
import deEntityTypes from '@/locales/de/entityTypes.json';
import slEntityTypes from '@/locales/sl/entityTypes.json';

// Custom hook to use the I18n context
export function useI18n() {
  const context = useContext(I18nContext);
  
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  
  return context;
}

// Helper function to get translated entity type name
// Usa le traduzioni dal database se disponibili, altrimenti fallback ai file JSON
// Per l'italiano, usa direttamente entityType.name senza cercare nei file JSON
export function getEntityTypeName(entityType: any, language: string): string {
  if (!entityType) return '';
  
  const code = entityType.code;
  if (!code) return entityType.name || '';
  
  // Per l'italiano, usa direttamente entityType.name (senza cercare nei file JSON)
  if (language !== 'en' && language !== 'de' && language !== 'sl') {
    return entityType.name || '';
  }
  
  // Prima prova con le traduzioni dal database
  let translatedName: string | undefined;
  switch (language) {
    case 'en':
      translatedName = entityType.name_en || entityType.nameEn;
      break;
    case 'de':
      translatedName = entityType.name_de || entityType.nameDe;
      break;
    case 'sl':
      translatedName = entityType.name_sl || entityType.nameSl;
      break;
    default:
      translatedName = undefined;
  }
  
  // Se la traduzione dal database esiste, usala
  if (translatedName) {
    return translatedName;
  }
  
  // Fallback ai file JSON
  let jsonTranslations: Record<string, string>;
  switch (language) {
    case 'en':
      jsonTranslations = enEntityTypes as Record<string, string>;
      break;
    case 'de':
      jsonTranslations = deEntityTypes as Record<string, string>;
      break;
    case 'sl':
      jsonTranslations = slEntityTypes as Record<string, string>;
      break;
    default:
      jsonTranslations = itEntityTypes as Record<string, string>;
  }
  
  // Prova con il codice esatto, poi con varianti comuni
  const jsonName = jsonTranslations[code] || 
                   jsonTranslations[code.replace(/_/g, '-')] ||
                   jsonTranslations[code.replace(/-/g, '_')];
  
  if (jsonName) {
    return jsonName;
  }
  
  // Ultimo fallback: nome italiano dal database
  return entityType.name || '';
}

// Helper function to get translated entity type description
export function getEntityTypeDescription(entityType: any, language: string): string {
  if (!entityType) return '';
  
  switch (language) {
    case 'en':
      return entityType.description_en || entityType.descriptionEn || entityType.description || '';
    case 'de':
      return entityType.description_de || entityType.descriptionDe || entityType.description || '';
    case 'sl':
      return entityType.description_sl || entityType.descriptionSl || entityType.description || '';
    default:
      return entityType.description || '';
  }
}
